import Modal from "@components/atoms/Modal";
import styled from "styled-components/native";
import useScreenOptionsHooks from "../../../../hooks/useScreenOptionsHooks";
import { ScrollView } from "react-native";
import { useMemo, useState } from "react";
import { AppliedCoupon, computePlanPrice, formatToday, Plan } from "./utils";
import { useCreateContract, useUpdateContract } from "@apis/billing";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { useGlobalData } from "@redux/slices/globalSlice";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import ReviewChangesPopup from "./components/ReviewChangesPopUp";
import { useGetUser } from "@apis/user";
import { useUserAnalytics } from "../../../../hooks/useAnalytics";
import { EventName, EventType, ViewType, ViewTypeEnum } from "@utils/analytics";
import ManagePlanStep1 from "./ManagePlanStep1";
import ManagePlanStep2 from "./ManagePlanStep2";
import { VDBIcons } from "@utils/VDBIcons";

const ManagePlanComponent = ({
  onClose,
  setShowSuccessPopup,
  step,
  setStep,
}: {
  onClose: () => void;
  setShowSuccessPopup: (show: boolean) => void;
  step: number;
  setStep: (step: number) => void;
}) => {
  const { user, isUpcomingNotPaidPlan } = useUserInfo();
  const today = formatToday();
  const globalData = useGlobalData();
  const plansData = globalData?.subscription_config;
  const isModifyPlan = isUpcomingNotPaidPlan();
  const getSelectedPlan = () => {
    if (isModifyPlan) {
      return plansData?.plans.filter((plan: Plan) =>
        user?.plan_details?.zenskar_pricings?.some(
          (p: any) => plan.plan_id === p.product_id
        )
      )[0];
    } else {
      return null;
    }
  };

  const getAddedAdditionalUsers = () => {
    const pricings = user?.plan_details?.zenskar_pricings;

    if (!isModifyPlan || !Array.isArray(pricings)) return 0;

    const item = pricings.find((p) => Array.isArray(p?.pricing_quantity));

    if (!item) return 0;

    // Return total count of pricing_quantity user_count items
    return item.pricing_quantity.reduce(
      (sum, q) => sum + (Number(q.user_count) || 0),
      0
    );
  };
  const { t, theme, typography } = useScreenOptionsHooks();
  const [selectedPlanIndex, setSelectedPlanIndex] = useState<number>(
    isModifyPlan
      ? plansData?.plans.findIndex(
          (plan: Plan) => plan.plan_id === getSelectedPlan()?.plan_id
        )
      : null
  );
  const selectedPlan =
    selectedPlanIndex !== null ? plansData?.plans[selectedPlanIndex] : null;
  const [additionalUsers, setAdditionalUsers] = useState<number>(
    getAddedAdditionalUsers() || 0
  );
  const [showContactUsModal, setShowContactUsModal] = useState(false);
  const [showReviewChangesPopup, setShowReviewChangesPopup] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon>(null);
  const disableReviewButton =
    additionalUsers === getAddedAdditionalUsers() &&
    selectedPlan?.plan_type === getSelectedPlan()?.plan_type;
  const createContractMutation = isModifyPlan
    ? useUpdateContract()
    : useCreateContract();

  const [userId, setUserId] = useState(user.id);
  const {} = useGetUser(userId, setUserId);
  const { trackButtonEvent } = useUserAnalytics();
  const handleSelectPlan = (index: number) => {
    trackButtonEvent(
      EventName.BUTTON_CLICK,
      index === 0
        ? EventType.PLAN_1_SELECTED_BUTTON_CLICK
        : EventType.PLAN_2_SELECTED_BUTTON_CLICK,
      { component_type: ViewType.POP_UP },
      ViewTypeEnum?.MANAGE_PLAN_POP_UP
    );
    setSelectedPlanIndex(index);
    if (!isModifyPlan) {
      setAdditionalUsers(0);
    }
  };
  const handleReviewChangesPopup = () => {
    setShowReviewChangesPopup((prev: boolean) => !prev);
  };
  const totals = useMemo(
    () =>
      computePlanPrice(
        selectedPlan,
        additionalUsers,
        user?.is_tax_applicable,
        appliedCoupon
      ),
    [selectedPlan, additionalUsers, appliedCoupon]
  );
  const lines = useMemo(() => {
    const feeLabel = `${t(
      "managePlan.popUp.creditCardFee.text",
      "Processing Fee"
    )} (${selectedPlan?.credit_card_fee_percent ?? 0}%)`;
    const taxLabel = `${t("managePlan.popUp.taxes.text", "Taxes")} (${
      selectedPlan?.tax_percent ?? 0
    }%)`;
    return [
      {
        label: `${t("managePlan.popUp.basePlan.text", "Base Plan")} - ${
          selectedPlan?.label
        }`,
        value: totals?.planBase,
      },
      {
        label: `${t(
          "managePlan.popUp.additionalUsers.text",
          "Additional Users"
        )} ${selectedPlan?.label} (${additionalUsers})`,
        value: totals?.addUsersCost,
      },
      ...(appliedCoupon?.coupon_code && (totals?.couponDiscount ?? 0) > 0
        ? [
            {
              label:
                appliedCoupon.discount_type === "percent"
                  ? `${t(
                      "managePlan.popUp.coupon.discount.text",
                      "Coupon discount"
                    )} (${appliedCoupon.discount_value}%)`
                  : t(
                      "managePlan.popUp.coupon.discount.text",
                      "Coupon discount"
                    ),
              value: -(totals?.couponDiscount ?? 0),
            },
          ]
        : []),
      ...(user?.is_tax_applicable
        ? [{ label: taxLabel, value: totals?.tax }]
        : []),
      {
        label: t("managePlan.popUp.subtotal.text", "Subtotal"),
        value: totals?.taxedTotal,
      },
      { label: feeLabel, value: totals?.fee },
    ];
  }, [
    selectedPlan,
    totals,
    additionalUsers,
    user?.is_tax_applicable,
    appliedCoupon,
  ]);

  const handleCreateContract = (type: "confirm_plan" | "modify_plan") => {
    trackButtonEvent(
      EventName.BUTTON_CLICK,
      EventType.CONFIRM_PLAN_BUTTON_CLICK,
      { component_type: ViewType.POP_UP },
      ViewTypeEnum?.MANAGE_PLAN_POP_UP
    );
    const couponCodeFor = (productId: any) =>
      appliedCoupon?.coupon_code &&
      (appliedCoupon?.applicable_product_ids || [])
        .map(String)
        .includes(String(productId))
        ? { coupon_code: appliedCoupon.coupon_code }
        : {};

    const payload: { plan_type: string; pricings: any[] } =
      type === "confirm_plan"
        ? {
            plan_type: selectedPlan?.plan_type,
            ...(user.is_tax_applicable && { taxes: selectedPlan?.tax_percent }),
            pricings: [
              {
                product_id: selectedPlan?.plan_id,
                pricing_id: selectedPlan?.pricing_id,
                amount: selectedPlan?.base_price,
                ...couponCodeFor(selectedPlan?.plan_id),
              },
              ...(additionalUsers > 0
                ? [
                    {
                      product_id: selectedPlan?.additional_user_plan?.id,
                      pricing_id:
                        selectedPlan?.additional_user_plan?.pricing_id,
                      pricing_quantity: additionalUsers,
                      amount: selectedPlan?.additional_user_plan?.base_price,
                      ...couponCodeFor(selectedPlan?.additional_user_plan?.id),
                    },
                  ]
                : []),
            ],
          }
        : {
            plan_type: selectedPlan?.plan_type,
            pricings: [
              {
                product_id: selectedPlan?.plan_id,
                ...couponCodeFor(selectedPlan?.plan_id),
              },
              ...(additionalUsers > 0
                ? [
                    {
                      product_id: selectedPlan?.additional_user_plan?.id,
                      pricing_quantity: [
                        {
                          date: today,
                          user_count: additionalUsers,
                        },
                      ],
                      ...couponCodeFor(selectedPlan?.additional_user_plan?.id),
                    },
                  ]
                : []),
            ],
          };
    createContractMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data.data.status === "skipped") {
          showToast(undefined, data.data.message, ToastTypes.ERROR, true, true);
          setUserId(user.id);
          setTimeout(() => {
            onClose();
          }, 4000);
        } else {
          setShowSuccessPopup(true);
          onClose();
        }
      },
      onError: (error: any) => {
        showToast(undefined, error.data.message, ToastTypes.ERROR, true, true);
      },
    });
  };
  return (
    <>
      <ScrollView
        scrollEnabled={theme.isMobile ? true : false}
        showsVerticalScrollIndicator={false}
      >
        <Overlay>
          {step === 1 && (
            <ManagePlanStep1
              plansData={plansData}
              selectedPlanIndex={selectedPlanIndex}
              handleSelectPlan={handleSelectPlan}
              additionalUsers={additionalUsers}
              setAdditionalUsers={setAdditionalUsers}
              selectedPlan={selectedPlan}
              t={t}
              typography={typography}
              theme={theme}
              trackButtonEvent={trackButtonEvent}
              setShowContactUsModal={setShowContactUsModal}
              showContactUsModal={showContactUsModal}
              setStep={setStep}
              disableReviewButton={disableReviewButton}
            />
          )}
          {step === 2 && (
            <ManagePlanStep2
              lines={lines}
              totals={totals}
              t={t}
              typography={typography}
              theme={theme}
              trackButtonEvent={trackButtonEvent}
              setShowContactUsModal={setShowContactUsModal}
              showContactUsModal={showContactUsModal}
              handleCreateContract={handleCreateContract}
              createContractMutation={createContractMutation}
              disableReviewButton={disableReviewButton}
              handleReviewChangesPopup={handleReviewChangesPopup}
              isModifyPlan={isModifyPlan}
              productIds={[
                selectedPlan?.plan_id,
                ...(additionalUsers > 0
                  ? [selectedPlan?.additional_user_plan?.id]
                  : []),
              ].filter(Boolean)}
              appliedCoupon={appliedCoupon}
              setAppliedCoupon={setAppliedCoupon}
            />
          )}
        </Overlay>
      </ScrollView>
      <ReviewChangesPopup
        isOpen={showReviewChangesPopup}
        onClose={handleReviewChangesPopup}
        handleCreateContract={handleCreateContract}
        selectedPlan={getSelectedPlan()}
        additionalUsers={getAddedAdditionalUsers()}
        updatedPlan={selectedPlan}
        updatedAdditionalUsers={additionalUsers}
        lines={lines}
        totals={totals}
        isLoading={createContractMutation.isPending}
      />
    </>
  );
};

const ManagePlanPopUp = ({
  isOpen,
  onClose,
  setShowSuccessPopup,
}: {
  isOpen: boolean;
  onClose: () => void;
  setShowSuccessPopup: (show: boolean) => void;
}) => {
  const { t } = useScreenOptionsHooks();
  const { isUpcomingNotPaidPlan } = useUserInfo();
  const isModifyPlan = isUpcomingNotPaidPlan();
  const [selectedStep, setSelectedStep] = useState<number>(1);
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setSelectedStep(1);
      }}
      paddingLeft={0}
      paddingRight={0}
      paddingBottom={0}
      paddingTop={0}
      headerTitle={
        isModifyPlan
          ? t("modifyPlan.popUp.title.text", "Modify Plan")
          : t("managePlan.popUp.title.text", "Manage Plan")
      }
      enableToastMessage={true}
      type={"popUp"}
      size={"lg"}
      noPadding
      showScrollbar={false}
      skipKeyboardAwareWeb={true}
      {...(selectedStep === 2 && {
        leftIcon: VDBIcons.LEFT,
        leftIconPress: () => setSelectedStep(1),
      })}
    >
      <ManagePlanComponent
        onClose={onClose}
        setShowSuccessPopup={setShowSuccessPopup}
        step={selectedStep}
        setStep={setSelectedStep}
      />
    </Modal>
  );
};

const Overlay = styled.View`
  flex-direction: ${({ theme }: { theme: any }) =>
    theme.isMobile ? "column" : "column"};
`;

export default ManagePlanPopUp;
